import { isDesignStudioInIframe } from 'src/modules/designStudio/helpers/fullscreenIframe';
import { useQueryParam } from 'src/shared/hooks';

const FULLSCREEN_ENABLED_PARAM = 'toggle_fs';
const ONLY_FULLSCREEN_TOOLBAR_PARAM = 'fs_toolbar_only';

interface FullscreenConfig {
  shouldDisplayFullscreenButton: boolean;
  onlyDisplayToolbarInFullscreen: boolean;
}

const useFullscreenConfig = (): FullscreenConfig => {
  /**
   * This hook checks for specific query params to determine the
   * behavior of Fullscreen mode when displayed in an iframe:
   *
   * 'toggle_fs': When 'on' the fullscreen button will be displayed
   * in the top right of the iframe. When the user clicks the button,
   * Design Studio will enter fullscreen mode.
   *
   * 'fs_toolbar_only': When 'on' the toolbar will only be displayed
   * if the iframe is in fullscreen mode. This is intended to be used
   * where there isn't much screen real estate and the toolbar would
   * look crowded, or would need scroll behavior. This can only be
   * used when toggle_fs is enabled (ability to enter fullscreen mode)
   */
  const fullscreenParamEnabled =
    useQueryParam(FULLSCREEN_ENABLED_PARAM) === 'on';
  const onlyDisplayToolbarInFullscreenEnabled =
    useQueryParam(ONLY_FULLSCREEN_TOOLBAR_PARAM) === 'on';
  const designStudioIsInIframe = isDesignStudioInIframe();

  const shouldDisplayFullscreenButton =
    designStudioIsInIframe && fullscreenParamEnabled;
  const onlyDisplayToolbarInFullscreen =
    shouldDisplayFullscreenButton && onlyDisplayToolbarInFullscreenEnabled;

  return { shouldDisplayFullscreenButton, onlyDisplayToolbarInFullscreen };
};

export {
  FULLSCREEN_ENABLED_PARAM,
  ONLY_FULLSCREEN_TOOLBAR_PARAM,
  useFullscreenConfig,
};
